/* The shape of your day, and the things that repeat in it.
 *
 * This is the part that went missing when the client switched. Sleep, working
 * hours and rituals all existed on the server the whole time and all had a
 * screen in the prototype; the React client had none, so the moment production
 * started serving it, nobody could say when they sleep. For a planner that is
 * not a missing preference — it is the app planning somebody else's life,
 * because every one of those numbers has a default and the defaults are a
 * guess.
 *
 * Three things, in the order they constrain the day:
 *
 *   1. **Sleep**, which nothing may be scheduled into. Not a preference the
 *      solver weighs — a window it cannot enter, for you as much as for the
 *      agent.
 *   2. **Working hours**, which decide where work may go and, by their
 *      absence, where a hobby can.
 *   3. **Rituals** — the standing weekly commitments: the gym on Tuesday, the
 *      school run every weekday, Friday night. A ritual is how you tell the
 *      planner that an hour is already spoken for before it starts offering
 *      your week to work.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ActivityType } from '@shared/types';
import { get, post, put, patch, del, errorMessage } from '../../api';
import { useSession } from '../../session';
import { useToast } from '../../ui/toast';
import { hhmm, toMinuteOfDay, human } from '../../ui/time';
import { ConfirmDialog } from '../../ui/schedule';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';

interface Ritual {
  id: string;
  title: string;
  kind: 'ritual' | 'break';
  startMin: number;
  endMin: number;
  /** 0 = Sunday, as the server stores it. */
  days: number[];
  protection: 'hard' | 'soft';
  typeId: string | null;
}

/* Monday first, because that is the week people live in; the values underneath
   stay 0-6 with Sunday at 0, because that is what the server stores. */
const WEEK = [
  { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' }
];

export default function Shape() {
  const { user, refresh } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [dropping, setDropping] = useState<Ritual | null>(null);

  const rituals = useQuery({
    queryKey: ['rituals'],
    queryFn: async () => {
      const r = await get<{ rituals: Ritual[] }>('/api/rituals');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your rituals.'));
      return r.rituals;
    }
  });

  const types = useQuery({
    queryKey: ['activity-types'],
    queryFn: async () => {
      const r = await get<{ types: ActivityType[] }>('/api/activity-types');
      return r.ok ? r.types : [];
    }
  });

  const saveSleep = useMutation({
    mutationFn: async (body: { start?: number; end?: number }) => {
      const r = await put('/api/sleep', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return r;
    },
    onSuccess: () => {
      void refresh();
      toast.say('Sleep saved. Nothing will be planned into it — not by you, not by Amon.', 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const saveHours = useMutation({
    mutationFn: async (body: { workStart?: number; workEnd?: number }) => {
      const r = await patch('/api/auth/me', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not save that.'));
      return r;
    },
    onSuccess: () => { void refresh(); toast.say('Working hours saved.', 'good'); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const addRitual = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const r = await post('/api/rituals', body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add that.'));
      return body.title as string;
    },
    onSuccess: title => {
      void qc.invalidateQueries({ queryKey: ['rituals'] });
      setAdding(false);
      toast.say(`${title} is held every week now.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const dropRitual = useMutation({
    mutationFn: async (r: Ritual) => {
      const res = await del(`/api/rituals/${r.id}`);
      if (!res.ok) throw new Error(errorMessage(res, 'Could not remove that.'));
      return r;
    },
    onSuccess: r => {
      void qc.invalidateQueries({ queryKey: ['rituals'] });
      toast.say(`${r.title} is no longer held.`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const list = rituals.data ?? [];

  return (
    <>
      <Section id="sleep-title" icon="moon" title="Sleep"
               meta={user ? `${hhmm(user.sleepStart)}–${hhmm(user.sleepEnd)} · nothing may be planned into it` : 'the one window nothing may enter'}
               info={<>Not a preference the planner weighs against your deadlines — a window it
                 cannot schedule into at all, and neither can you. Every other number here
                 is a judgement; this one is a wall.</>}>

        <form className="when-form" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          saveSleep.mutate({
            start: toMinuteOfDay((form.elements.namedItem('sleepStart') as HTMLInputElement).value),
            end: toMinuteOfDay((form.elements.namedItem('sleepEnd') as HTMLInputElement).value)
          });
        }}>
          <div className="field">
            <label htmlFor="sleepStart">Asleep by</label>
            <input id="sleepStart" name="sleepStart" type="time"
                   defaultValue={hhmm(user?.sleepStart ?? 1380)} />
          </div>
          <div className="field">
            <label htmlFor="sleepEnd">Awake at</label>
            <input id="sleepEnd" name="sleepEnd" type="time"
                   defaultValue={hhmm(user?.sleepEnd ?? 420)} />
          </div>
          <div className="inline field-wide">
            <button className="btn btn-primary btn-sm" type="submit" disabled={saveSleep.isPending}>
              {saveSleep.isPending ? 'Saving…' : 'Save sleep'}
            </button>
            <span className="muted small">
              {user ? `${human(((user.sleepEnd - user.sleepStart) + 1440) % 1440)} a night` : ''}
            </span>
          </div>
        </form>
      </Section>

      <Section id="hours-title" icon="briefcase" title="Working hours"
               meta={user ? `${hhmm(user.workStart)}–${hhmm(user.workEnd)} · work only goes here` : 'where work may go'}
               info={<>The planner only places work inside these. That is also what protects the
                 rest: an evening is free because work cannot reach it, so if you want a
                 hobby planned there, it needs a category marked as not-work rather than
                 longer hours.</>}>

        <form className="when-form" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          saveHours.mutate({
            workStart: toMinuteOfDay((form.elements.namedItem('workStart') as HTMLInputElement).value),
            workEnd: toMinuteOfDay((form.elements.namedItem('workEnd') as HTMLInputElement).value)
          });
        }}>
          <div className="field">
            <label htmlFor="workStart">Start</label>
            <input id="workStart" name="workStart" type="time"
                   defaultValue={hhmm(user?.workStart ?? 540)} />
          </div>
          <div className="field">
            <label htmlFor="workEnd">Stop</label>
            <input id="workEnd" name="workEnd" type="time"
                   defaultValue={hhmm(user?.workEnd ?? 1080)} />
          </div>
          <div className="inline field-wide">
            <button className="btn btn-primary btn-sm" type="submit" disabled={saveHours.isPending}>
              {saveHours.isPending ? 'Saving…' : 'Save hours'}
            </button>
          </div>
        </form>
      </Section>

      <Section id="rituals-title" icon="repeat" title="Rituals" className="tone-green"
               meta={list.length ? `${list.length} held every week` : 'the hours already spoken for'}
               info={<>The hours that are already spoken for every week: the gym on Tuesday, the
                 school run, Friday night. Amon holds them before it offers your week to
                 anything else — which is the difference between a hobby you intend and a
                 hobby that happens.</>}
               action={!adding && (
                 <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>
                   <Icon name="plus" size={16} />Add a ritual
                 </button>
               )}>

        {rituals.isError && (
          <p className="auth-err" role="alert">{(rituals.error as Error).message}</p>
        )}

        {adding && (
          <RitualForm
            types={types.data ?? []}
            onCancel={() => setAdding(false)}
            onSave={body => addRitual.mutate(body)}
            saving={addRitual.isPending} />
        )}

        {!rituals.isPending && list.length === 0 && !adding && (
          <p className="muted small">
            None yet, so every hour you are awake is offered to work first.
          </p>
        )}

        <ul className="ritual-list">
          {list.map(r => (
            <li key={r.id}>
              <span className="item-icon" aria-hidden="true">
                <Icon name={r.protection === 'hard' ? 'lock' : 'repeat'} size={17} />
              </span>
              <div className="item-body">
                <strong>{r.title}</strong>
                <span className="muted small">
                  {hhmm(r.startMin)}–{hhmm(r.endMin)} ·{' '}
                  {r.days.length === 7 ? 'every day'
                    : WEEK.filter(d => r.days.includes(d.value)).map(d => d.label).join(', ')}
                  {r.protection === 'hard' ? ' · never moved' : ' · gives way if it must'}
                </span>
              </div>
              <button className="btn btn-ghost btn-icon btn-bad" onClick={() => setDropping(r)}
                      aria-label="Remove" title={`Stop holding ${r.title}`}><Icon name="trash" size={17} /></button>
            </li>
          ))}
        </ul>
      </Section>

      {dropping && (
        <ConfirmDialog
          title={`Stop holding ${dropping.title}?`}
          description="The hour goes back to being ordinary time, and the planner may offer it to
                       work from the next replan."
          confirmLabel="Stop holding it"
          onClose={() => setDropping(null)}
          onConfirm={() => dropRitual.mutateAsync(dropping)} />
      )}
    </>
  );
}

function RitualForm({ types, onCancel, onSave, saving }: {
  types: ActivityType[];
  onCancel: () => void;
  onSave: (body: Record<string, unknown>) => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState('18:00');
  const [minutes, setMinutes] = useState('60');
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [protection, setProtection] = useState<'hard' | 'soft'>('soft');
  const [typeId, setTypeId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggle = (d: number) =>
    setDays(list => list.includes(d) ? list.filter(x => x !== d) : [...list, d]);

  return (
    <form className="when-form" onSubmit={(e: FormEvent) => {
      e.preventDefault();
      if (!title.trim()) { setError('What is it?'); return; }
      if (!days.length) { setError('Which days?'); return; }
      setError(null);
      const startMin = toMinuteOfDay(start);
      onSave({
        title: title.trim(),
        startMin,
        endMin: Math.min(1440, startMin + Math.max(15, Number(minutes) || 60)),
        days,
        protection,
        typeId: typeId || null
      });
    }}>
      <div className="field field-wide">
        <label htmlFor="ritual-title">What is it?</label>
        <input id="ritual-title" value={title} maxLength={120} autoFocus
               placeholder="The gym" onChange={e => setTitle(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="ritual-start">Starts</label>
        <input id="ritual-start" type="time" value={start}
               onChange={e => setStart(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="ritual-mins">Minutes</label>
        <input id="ritual-mins" type="number" min="15" max="480" step="15" value={minutes}
               onChange={e => setMinutes(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="ritual-type">Counts as</label>
        <select id="ritual-type" value={typeId} onChange={e => setTypeId(e.target.value)}>
          <option value="">— nothing in particular —</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <fieldset className="field-wide alarm-picker">
        <legend>Which days</legend>
        {WEEK.map(d => (
          <label key={d.value} className={`chip chip-pick${days.includes(d.value) ? ' is-on' : ''}`}>
            <input type="checkbox" checked={days.includes(d.value)}
                   onChange={() => toggle(d.value)} />
            {d.label}
          </label>
        ))}
      </fieldset>

      <div className="field field-wide">
        <label htmlFor="ritual-protection">If something clashes</label>
        <select id="ritual-protection" value={protection}
                onChange={e => setProtection(e.target.value as 'hard' | 'soft')}>
          <option value="soft">It can give way — tell me when it does</option>
          <option value="hard">Never move it</option>
        </select>
      </div>

      {error && <p className="auth-err field-wide" role="alert">{error}</p>}

      <div className="inline field-wide">
        <button className="btn btn-primary btn-sm" type="submit" disabled={saving}>
          {saving ? 'Saving…' : 'Hold this time'}
        </button>
        <button className="btn btn-ghost btn-sm" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
